<template>
  <div
    ref="element"
    class="product-card"
    :class="[
      $blockClass(blockClass, 'product-card'),
      {
        [$class('loading')]: state === 'loading',
        'product-card--sold-out': localProduct && !localProduct.available,
        'product-card--small': small,
        'product-card--tiny': tiny,
      },
    ]"
    @mouseenter="isMobile ? null : loadSecondaryImage = true"
    @mouseleave="isMobile ? null : loadSecondaryImage = false"
  >
    <div class="product-card__image-wrapper">
      <span
        class="visually-hidden"
        v-text="localProduct.product_title"
      />

      <div
        v-if="enableSwiper && images.length > 0"
        :key="localProduct.handle"
        class="product-card__images-wrapper"
      >
        <div
          class="product-card__images"
          :style="{ transform: `translateX(-${currentSlide * 100}%)` }"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <a
            v-for="(image, imageIndex) of images"
            :key="`images-${localProduct.handle}-${imageIndex}`"
            class="product-card__image-link"
            :class="{ [$class('active')]: imageIndex === currentSlide }"
            :href="localProduct.url"
            @click="handleClick"
          >
            <responsive-image
              :alt="getImageAlt(image)"
              class="product-card__responsive-image"
              :class="$blockClass(blockClass, 'product-card-image')"
              block-class="product-card"
              :image="state === 'ready' ? image : {}"
              min-max="200-650"
              ratio="3:4"
              :sizes="sizes"
              :style="{ animationDelay: animationDelay }"
              :lazyload="lazyload"
            />
          </a>
        </div>

        <ul
          class="product-card__images-pagination"
          role="tablist"
        >
          <li
            v-for="(image, imageIndex) of images"
            :key="`pagination-${localProduct.handle}-${imageIndex}`"
            role="presentation"
          >
            <button
              class="product-card__images-pagination-page"
              :class="{ [$class('active')]: imageIndex === currentSlide }"
              type="button"
              role="tab"
              tabindex="-1"
            />
          </li>
        </ul>

        <button
          ref="quickView"
          class="product-card__quickview icon-button"
          type="button"
          @click.prevent="handleQuickViewClick"
        >
          <span
            class="product-card__quickview-text text-label-s"
            v-text="$string('product.card.quickview')"
          />

          <icon-cart />
        </button>
      </div>

      <div
        v-else
        class="product-card__featured-image"
      >
        <a
          :key="`featured-image-${localProduct.handle}`"
          class="product-card__image-link"
          :href="localProduct.url"
          @click="handleClick"
        >
          <responsive-image
            :alt="getImageAlt(localProduct.featuredImage)"
            class="product-card__responsive-image"
            :class="$blockClass(blockClass, 'product-card-image')"
            block-class="product-card"
            :image="state === 'ready' ? localProduct.featuredImage : {}"
            min-max="200-650"
            ratio="3:4"
            :sizes="sizes"
            :style="{ animationDelay: animationDelay }"
            :lazyload="lazyload"
          />
        </a>

        <a
          v-if="localProduct?.images?.length > 1 && loadSecondaryImage"
          :key="`second-image-${localProduct.handle}`"
          class="product-card__image-link product-card__image-link--hover"
          :href="localProduct.url"
          @click="handleClick"
        >
          <responsive-image
            :alt="getImageAlt(localProduct.images[1])"
            class="product-card__responsive-hover-image"
            :class="$blockClass(blockClass, 'product-card-image')"
            block-class="product-card"
            :image="state === 'ready' ? localProduct.images[1] : {}"
            min-max="200-650"
            :sizes="sizes"
            :padding="false"
            :loaded-animation="false"
            :lazyload="lazyload"
          />
        </a>
      </div>

      <div
        v-if="enableSwiper"
        class="product-card__images-arrows"
      >
        <btn
          class="
            product-card__images-arrow
            product-card__images-arrow--prev
          "
          :label="$string('accessibility.carousel.previous')"
          modifiers="text,round"
          type="button"
          @click.prevent="handleArrowClick('previous')"
        >
          <template #icon>
            <icon-chevron-left />
          </template>
        </btn>

        <btn
          class="
            product-card__images-arrow
            product-card__images-arrow--next
          "
          :label="$string('accessibility.carousel.next')"
          modifiers="text,round"
          type="button"
          @click.prevent="handleArrowClick('next')"
        >
          <template #icon>
            <icon-chevron-right />
          </template>
        </btn>
      </div>

      <product-badges
        v-if="localProduct"
        :key="`badges-${localProduct.handle}`"
        class="product-card__badges"
        :badges="product?.badges"
        :product="localProduct"
        :tiny="tiny"
      />

      <wishlist-button
        v-if="localProduct?.variants?.[0] && localProduct?.handle"
        class="product-card__wishlist-button"
        :product="localProduct"
        template="card"
        :variant="localProduct.variants?.[0]"
      />

      <button
        v-if="!enableSwiper"
        ref="quickView"
        class="product-card__quickview icon-button"
        type="button"
        @click="handleQuickViewClick"
      >
        <span
          class="product-card__quickview-text text-label-s"
          v-text="$string('product.card.quickview')"
        />

        <icon-cart />
      </button>
    </div>

    <div
      class="product-card__footer"
      :class="$blockClass(blockClass, 'product-card-footer')"
    >
      <p
        v-if="localProduct.vendor || state === 'loading'"
        class="product-card__vendor text-heading-2xs"
        :class="[
          $blockClass(blockClass, 'product-card-vendor'),
          { 'text-heading-xs-desktop': !tiny },
        ]"
        :style="{ animationDelay: animationDelay }"
        @click="handleClick"
        v-text="localProduct.vendor"
      />

      <a
        class="product-card__title text-body-xs"
        :class="[
          $blockClass(blockClass, 'product-card-title'),
          { 'text-body-s-desktop': !tiny },
        ]"
        :href="localProduct.url"
        :style="{ animationDelay: animationDelay }"
        @click="handleClick"
        v-text="localProduct.product_title"
      />

      <div class="product-card__meta">
        <product-prices
          v-if="pricesReady"
          :class="$blockClass(blockClass, 'product-card-prices')"
          :class-normal="`text-heading-2xs ${tiny ? '' : 'text-heading-xs-desktop'}`"
          :class-sold-out="`text-body-xs ${tiny ? '' : 'text-body-s-desktop'}`"
          :available="localProduct?.available"
          block-class="product-card"
          :compare-at-price="localProduct?.compareAtPrice?.amount"
          :is-coming-soon="localProduct?.isComingSoon"
          :loading="localProduct?.price?.loading"
          :price="localProduct?.price?.amount"
          :style="{ animationDelay: animationDelay }"
          :varies="localProduct?.price?.varies"
        />

        <colour-swatches
          v-if="swatchesReady"
          class="product-card__swatches"
          :animation-delay="animationDelay"
          :colours="localColours"
          :colours-loaded="localProduct.coloursLoaded"
          :handle="localProduct.handle"
          limited
          :state="state"
          :visible="visible"
          @update-handle="handleUpdateHandle"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product card
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Boolean} [delayClick] - Delay click event to save/submit data.
 * @param {Number} [index] - Product card's forloop index.
 * @param {Boolean|String} [nostoResultId] - Nosto result Id for tracking.
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Boolean} [small] - Move colour swatches below footer on mobile.
 * @param {Boolean} [swiperCarousel] - Enable/disable Swiper carousel.
 * @param {Boolean} [tiny] - Hide colour swatches and use smaller font sizes.
 * @param {Boolean} [visible] - Product card is visible in carousel.
 * @param {Boolean} [lazyload] - Lazy loading for product images
 *
 */
import { mapActions, mapState } from 'vuex'
import { nextTick } from 'vue'

import Btn from '~global/btn/btn'
import ColourSwatches from '~async/colour-swatches/colour-swatches'
import ProductBadges from '~async/product-badges/product-badges'
import ProductPrices from '~global/product-prices/product-prices'
import WishlistButton from '~async/wishlist-button/wishlist-button'
import ResponsiveImage from '~global/images/responsive-image'

import IconCart from '~icons/misc/cart.svg'
import IconChevronLeft from '~icons/direction/chevron/left.svg'
import IconChevronRight from '~icons/direction/chevron/right.svg'

import breakpoints from '~/config/breakpoints'
import query from '~/graphql/queries/product.gql'
import { formatProduct } from '~/helpers/format'
import { setIntersectionObserver } from '~/helpers/general'

export default {
  name: 'ProductCard',

  components: {
    Btn,
    ColourSwatches,
    IconCart,
    IconChevronLeft,
    IconChevronRight,
    ProductBadges,
    ProductPrices,
    WishlistButton,
    ResponsiveImage,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    handle: {
      type: String,
      default: '',
    },
    delayClick: {
      type: Boolean,
      default: false,
    },
    index: {
      type: Number,
      default: 0,
    },
    nostoResultId: {
      type: [Boolean, String],
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    small: {
      type: Boolean,
      default: false,
    },
    swiperCarousel: {
      type: Boolean,
      default: true,
    },
    tiny: {
      type: Boolean,
      default: false,
    },
    visible: {
      type: Boolean,
      default: true,
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
  },

  emits: {
    'view-product': (payload) => typeof payload === 'string',
    'quick-view-product': (payload) => typeof payload === 'string',
  },

  data() {
    return {
      displayed: false,
      loadSecondaryImage: false,
      localColours: false,
      localProduct: false,
      currentSlide: 0,
      touchStartX: 0,
      touchStartY: 0,
      touchEndX: 0,
      touchEndY: 0,
      isHorizontalSwipe: false,
      state: 'ready',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute animation delay.
     * @returns {String}
     */
    animationDelay() {
      return this.$timing('quick', { index: this.index, unit: 's' })
    },

    /**
     * Compute if quickview is open.
     * @returns {Boolean}
     */
    quickviewIsOpen() {
      return this.quickview !== ''
    },

    /**
     * Compute enable swiper.
     * @returns {Boolean}
     */
    enableSwiper() {
      if (!this.swiperCarousel || !this.displayed) {
        return false
      }

      if (this.isMobile) {
        return cnvs.settings.enableProductCardSwipeMobile
      }

      return cnvs.settings.enableProductCardSwipeDesktop
    },

    /**
     * Compute swiper range.
     * @returns {Number}
     */
    swiperRange() {
      if (this.isMobile) {
        return cnvs.settings.productCardSwipeMobile
      }

      return cnvs.settings.productCardSwipeDesktop
    },

    /**
     * Compute swiper images.
     * @returns {Array}
     */
    images() {
      if (!this.localProduct || !this.enableSwiper) {
        return []
      }

      return this.localProduct.images.slice(0, this.swiperRange)
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.l}) 25vw, 100vw`
    },

    /**
     * Render prices only when localised price payload is ready.
     * @returns {Boolean}
     */
    pricesReady() {
      return Boolean(
        this.localProduct?.price &&
        this.localProduct.price.loading === false
      )
    },

    /**
     * Render swatches only when colour availability has loaded.
     * @returns {Boolean}
     */
    swatchesReady() {
      return Boolean(
        this.localProduct?.coloursLoaded &&
        this.localColours &&
        Object.keys(this.localColours).length > 1 &&
        !this.tiny
      )
    },
  },

  watch: {

    /**
     * Watch product updates to update local product.
     * - Update state when false.
     */
    product: {
      handler() {
        nextTick().then(() => {
          if (!this.product && this.handle) {
            this.queryProduct(this.handle)
            return
          }

          this.setLocalProduct()
          this.state = this.localProduct === false ? 'loading' : 'ready'
        })
      },
      immediate: true,
    },
  },

  mounted() {
    setIntersectionObserver({
      callback: this.handleIntersection,
      element: this.$refs.element,
      threshold: [0, 1],
    })

    if (!cnvs.storybook || this.localProduct) {
      return
    }

    if (!this.product) {
      this.state = 'loading'
      return
    }

    this.setLocalProduct()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setQuickViewHandle: 'quick-view/setHandle',
    }),

    /**
     * Set local product.
     */
    setLocalProduct() {
      if (
        this.localProduct.handle === this.product.handle &&
        this.localProduct.price?.loading === this.product.price?.loading &&
        this.localProduct.coloursLoaded === this.product.coloursLoaded
      ) {
        return
      }

      this.localProduct = this.product
      this.localColours = this.product.colours
      this.setStorage(this.localProduct)
    },

    /**
     * Return image alt text. Default to product title if alt text was not
     * set in Shopify.
     * @param {Object} image - image object.
     * @returns {String}
     */
    getImageAlt(image) {
      return this.state === 'ready' && image?.alt
        ? image.alt
        : this.localProduct.product_title
    },

    /**
     * Update display product based on handle emit.
     * @param {String} handle - New handle to load.
     */
    handleUpdateHandle(handle) {
      if (handle === false || handle === this.localProduct.handle) {
        return
      }

      if (
        !cnvs.settings.disableProductSaving &&
        sessionStorage.getItem(`cnvs-product-${handle}`)
      ) {
        this.localProduct = JSON.parse(sessionStorage.getItem(`cnvs-product-${handle}`))
        return
      }

      this.queryProduct(handle)
    },

    /**
     * Query product.
     * @param {String} handle - Handle to load.
     */
    async queryProduct(handle) {
      this.state = 'loading'

      try {
        const response = await cnvs.Query({
          query,
          variables: {
            country: cnvs.store.country,
            handle,
            language: cnvs.store.language,
          },
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        this.localProduct = formatProduct(response)
        this.setStorage(this.localProduct)
        this.state = 'ready'

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Set product to sessionStorage.
     * @param {Object} product - Product to update.
     */
    setStorage(product) {
      if (cnvs.settings.disableProductSaving) {
        return
      }

      sessionStorage.setItem(
        `cnvs-product-${product.handle}`,
        JSON.stringify(product),
      )
    },

    /**
     * Handle product card click.
     * - Prevents click so collection data can be saved first or Nosto analytics
     *  data submitted on search.
     * @param {Object} event - Click event.
     */
    handleClick(event) {
      if (!this.delayClick) {
        return
      }

      event.preventDefault()
      this.$emit('view-product', this.localProduct.url, this.localProduct.id.toString())
    },

    /**
     * Handle quick view button click.
     * - Opens the quick view modal.
     * - Removes any existing colour order data from session storage.
     */
    handleQuickViewClick() {
      const data = {
        handle: this.localProduct.handle,
        resultId: this.nostoResultId,
      }
      this.setQuickViewHandle(data)
      sessionStorage.removeItem('cnvs-quickview-colour-order')
      this.$refs.quickView.blur()
      this.$emit('quick-view-product')
    },

    /**
     * Handle arrow click.
     * - Using custom function because if we use 'rewind' then it skips any
     *   incomplete pages, if we use 'loop' then it fills empty spaces with
     *   improperly initialised product card components.
     * @param {String} type - Type of arrow, `next` or `previous`.
     */
    handleArrowClick(type) {
      if (type === 'next') {
        this.currentSlide = (this.currentSlide + 1) % this.images.length

        return
      }

      this.currentSlide = (this.currentSlide - 1 + this.images.length) % this.images.length
    },

    /**
     * Handle touch start.
     * @param {Object} event -Event.
     */
    handleTouchStart(event) {
      this.touchStartX = event.touches[0].screenX
      this.touchStartY = event.touches[0].clientY
      this.isHorizontalSwipe = false
    },

    /**
     * Handle touch move.
     * @param {Object} event -Event.
     */
    handleTouchMove(event) {
      const moveX = event.touches[0].clientX
      const moveY = event.touches[0].clientY

      const dx = moveX - this.touchStartX
      const dy = moveY - this.touchStartY

      if (Math.abs(dx) > Math.abs(dy)) {
        this.isHorizontalSwipe = true
      }

      if (this.isHorizontalSwipe) {
        event.preventDefault()
        this.touchEndX = moveX
      }
    },

    /**
     * Handle touch end.
     */
    handleTouchEnd() {
      if (!this.isHorizontalSwipe) {
        return
      }

      const delta = this.touchEndX - this.touchStartX
      const threshold = 50

      if (Math.abs(delta) > threshold) {
        if (delta > 0) {
          this.handleArrowClick('prev')
        } else {
          this.handleArrowClick('next')
        }
      }
    },

    /**
     * Handle element intersection.
     * - Initialize the swiper after the element displayed.
     * @param {Array} entries
     */
    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.displayed = true
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
